export type AddressbookTableRow = {
  key?: string;
  accountId?: string;
  id?: string | number;
  modified?: string;
  contacts_count?: number;
  partyIdDirty?: boolean;
  partyIdValid?: boolean;
  communityLeaderId?: string | null;
  haspartyid_processed_accountid_addressbookid?: string;
  haspartyid_processed_addressbookid?: string;
  [field: string]: unknown;
};

const rowKey = (
  value: AddressbookTableRow | undefined,
  fallbackKey: string
): string => {
  if (value?.key) {
    return String(value.key);
  }
  if (value?.accountId != null && value?.id != null) {
    return `${value.accountId}_${value.id}`;
  }
  return fallbackKey;
};

const parseModified = (modified: string | undefined): number | null => {
  if (!modified) {
    return null;
  }
  const parsed = Date.parse(modified);
  return Number.isNaN(parsed) ? null : parsed;
};

export const isLocalAddressbookRowNewer = (
  existing: AddressbookTableRow,
  incoming: AddressbookTableRow
): boolean => {
  const existingModified = parseModified(
    typeof existing.modified === 'string' ? existing.modified : undefined
  );
  if (existingModified === null) {
    return false;
  }
  const incomingModified = parseModified(
    typeof incoming.modified === 'string' ? incoming.modified : undefined
  );
  if (incomingModified === null) {
    return true;
  }
  return existingModified >= incomingModified;
};

export const mapFilteredAddressbooksToRows = (
  filteredValues: unknown,
  addressbooksMetaValues?: unknown
): AddressbookTableRow[] => {
  if (!filteredValues || typeof filteredValues !== 'object') {
    return [];
  }

  const rows = filteredValues as Record<
    string,
    AddressbookTableRow | undefined
  >;
  const metaValues = addressbooksMetaValues as
    | Record<string, Record<string, { contacts_count?: number }>>
    | null
    | undefined;

  return Object.keys(rows)
    .filter((k) => rows[k])
    .map((k) => {
      const value = rows[k];
      if (!value) {
        return {};
      }
      const key = rowKey(value, k);
      const meta = metaValues?.[String(value.accountId)]?.[String(value.id)];
      return {
        ...value,
        key,
        contacts_count: meta?.contacts_count || 0,
      };
    });
};

/**
 * Rebuild table rows from a filter snapshot without wiping in-progress edits.
 * Filter results are a one-shot get(), so a later meta refresh can otherwise
 * restore a stale party_id / Processed flag after a successful save.
 */
export const mergeAddressbookTableRows = (
  incomingRows: AddressbookTableRow[],
  existingRows: AddressbookTableRow[]
): AddressbookTableRow[] => {
  const existingByKey = new Map<string, AddressbookTableRow>();
  existingRows.forEach((row) => {
    if (row?.key) {
      existingByKey.set(String(row.key), row);
    }
  });

  return incomingRows.map((incoming) => {
    if (!incoming?.key) {
      return incoming;
    }
    const existing = existingByKey.get(String(incoming.key));
    if (!existing || !isLocalAddressbookRowNewer(existing, incoming)) {
      return incoming;
    }
    return {
      ...incoming,
      ...existing,
      contacts_count: incoming.contacts_count,
    };
  });
};

export const applyAddressbookContactsCount = (
  rows: AddressbookTableRow[],
  addressbooksMetaValues?: unknown
): AddressbookTableRow[] => {
  const metaValues = addressbooksMetaValues as
    | Record<string, Record<string, { contacts_count?: number }>>
    | null
    | undefined;
  return rows.map((row) => ({
    ...row,
    contacts_count:
      metaValues?.[String(row.accountId)]?.[String(row.id)]?.contacts_count ??
      row.contacts_count ??
      0,
  }));
};
